import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, Company, LoginFormData, RegisterFormData } from '@/types';
import { authApi } from '@/services/api';
import { STORAGE_KEYS } from '@/utils/constants';

// =====================================================
// TYPES
// =====================================================

interface AuthState {
  user: User | null;
  company: Company | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  loginAttempts: number;
  lastLoginAttempt: number | null;
  twoFactorRequired: boolean;
  twoFactorToken: string | null;
  twoFactorSetupRequired: boolean;
  twoFactorQrCode: string | null;
  twoFactorSecret: string | null;
}

// Recuperar dados do localStorage se existirem
const storedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA);
const storedCompany = localStorage.getItem(STORAGE_KEYS.COMPANY_DATA);
const storedToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
const storedRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

const initialState: AuthState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  company: storedCompany ? JSON.parse(storedCompany) : null,
  token: storedToken || null,
  refreshToken: storedRefreshToken || null,
  isAuthenticated: !!(storedUser && storedToken),
  isLoading: false,
  error: null,
  loginAttempts: 0, // Sempre inicia em 0
  lastLoginAttempt: null, // Sempre inicia null
  twoFactorRequired: false,
  twoFactorToken: null,
  twoFactorSetupRequired: false,
  twoFactorQrCode: null,
  twoFactorSecret: null,
};

// =====================================================
// ASYNC THUNKS
// =====================================================

// Login
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginFormData, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials.email, credentials.password);

      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro no login');
      }

      const {
        user,
        company,
        token,
        refreshToken,
        twoFactorRequired,
        twoFactorToken,
        twoFactorSetupRequired,
        qrCode,
        secret
      } = response.data;

      // Se 2FA é necessário (seja verificação ou setup), não salva tokens ainda
      if (twoFactorRequired) {
        return {
          twoFactorRequired: true,
          twoFactorToken,
          twoFactorSetupRequired: twoFactorSetupRequired || false,
          twoFactorQrCode: qrCode || null,
          twoFactorSecret: secret || null,
          user: null,
          company: null,
          token: null,
          refreshToken: null,
        };
      }

      // Salva tokens no localStorage
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(company));

      return {
        user,
        company,
        token,
        refreshToken,
        twoFactorRequired: false,
        twoFactorToken: null,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro no login');
    }
  }
);

// Google Login
export const googleLogin = createAsyncThunk(
  'auth/googleLogin',
  async (credential: string, { rejectWithValue }) => {
    try {
      const response = await authApi.googleLogin(credential);

      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro no login com Google');
      }

      const {
        user,
        company,
        token,
        refreshToken,
        twoFactorRequired,
        twoFactorToken,
        twoFactorSetupRequired,
        qrCode,
        secret
      } = response.data;

      // Se 2FA é necessário, não salva tokens ainda
      if (twoFactorRequired) {
        return {
          twoFactorRequired: true,
          twoFactorToken,
          twoFactorSetupRequired: twoFactorSetupRequired || false,
          twoFactorQrCode: qrCode || null,
          twoFactorSecret: secret || null,
          user: null,
          company: null,
          token: null,
          refreshToken: null,
        };
      }

      // Salva tokens no localStorage
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(company));

      return {
        user,
        company,
        token,
        refreshToken,
        twoFactorRequired: false,
        twoFactorToken: null,
        twoFactorSetupRequired: false,
        twoFactorQrCode: null,
        twoFactorSecret: null,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro no login com Google');
    }
  }
);

// Verificar 2FA
export const verify2FA = createAsyncThunk(
  'auth/verify2FA',
  async ({ code, twoFactorToken }: { code: string; twoFactorToken: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.verify2FA(code, twoFactorToken);

      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Código inválido');
      }

      const { user, company, token, refreshToken, backupCodes } = response.data;

      // Salva tokens no localStorage
      if (token) {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      }
      if (refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      }
      if (user) {
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      }
      if (company) {
        localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(company));
      }

      return { user, company, token, refreshToken, backupCodes };
    } catch (error: any) {
      return rejectWithValue(error.error?.message || error.message || 'Erro na verificação 2FA');
    }
  }
);

// Registro
export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterFormData, { rejectWithValue }) => {
    try {
      const response = await authApi.register(userData);
      
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro no cadastro');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro no cadastro');
    }
  }
);

// Logout
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authApi.logout();
    } catch (error) {
      // Mesmo se der erro na API, vamos fazer logout local
      console.warn('Erro ao fazer logout na API:', error);
    } finally {
      // Limpa dados locais
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.COMPANY_DATA);
    }
    
    return null;
  }
);

// Refresh token
export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const refreshTokenValue = state.auth.refreshToken;
      
      if (!refreshTokenValue) {
        return rejectWithValue('Refresh token não encontrado');
      }

      const response = await authApi.refreshToken(refreshTokenValue);
      
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao renovar token');
      }

      const { token, refreshToken: newRefreshToken } = response.data;

      // Atualiza tokens no localStorage
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      if (newRefreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
      }

      return { token, refreshToken: newRefreshToken || refreshTokenValue };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao renovar token');
    }
  }
);

// Carregar dados do usuário
export const loadUserData = createAsyncThunk(
  'auth/loadUserData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.me();
      
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao carregar dados');
      }

      const { user, company } = response.data;

      // Atualiza localStorage
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(company));

      return { user, company };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao carregar dados');
    }
  }
);

// Esqueci minha senha
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await authApi.forgotPassword(email);
      
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao enviar email');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao enviar email');
    }
  }
);

// Reset de senha
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, password }: { token: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.resetPassword(token, password);
      
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Erro ao redefinir senha');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao redefinir senha');
    }
  }
);

// =====================================================
// SLICE
// =====================================================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Limpar erro
    clearError: (state) => {
      state.error = null;
    },

    // Limpar estado 2FA
    clear2FA: (state) => {
      state.twoFactorRequired = false;
      state.twoFactorToken = null;
      state.twoFactorSetupRequired = false;
      state.twoFactorQrCode = null;
      state.twoFactorSecret = null;
    },

    // Setar dados de autenticação (para persistência)
    setAuthData: (state, action: PayloadAction<{
      user: User | null;
      company: Company | null;
      token: string | null;
      refreshToken: string | null;
    }>) => {
      const { user, company, token, refreshToken } = action.payload;
      state.user = user;
      state.company = company;
      state.token = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = !!(user && token);
    },

    // Atualizar dados do usuário
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(state.user));
      }
    },

    // Atualizar dados da empresa
    updateCompany: (state, action: PayloadAction<Partial<Company>>) => {
      if (state.company) {
        state.company = { ...state.company, ...action.payload };
        localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(state.company));
      }
    },

    // Incrementar tentativas de login
    incrementLoginAttempts: (state) => {
      state.loginAttempts += 1;
      state.lastLoginAttempt = Date.now();
    },

    // Reset tentativas de login
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0;
      state.lastLoginAttempt = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.loginAttempts = 0;
        state.lastLoginAttempt = null;

        if (action.payload.twoFactorRequired) {
          state.twoFactorRequired = true;
          state.twoFactorToken = action.payload.twoFactorToken;
          state.twoFactorSetupRequired = action.payload.twoFactorSetupRequired || false;
          state.twoFactorQrCode = action.payload.twoFactorQrCode || null;
          state.twoFactorSecret = action.payload.twoFactorSecret || null;
        } else {
          state.user = action.payload.user;
          state.company = action.payload.company;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.twoFactorRequired = false;
          state.twoFactorToken = null;
          state.twoFactorSetupRequired = false;
          state.twoFactorQrCode = null;
          state.twoFactorSecret = null;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.loginAttempts += 1;
        state.lastLoginAttempt = Date.now();
      });

    // Google Login
    builder
      .addCase(googleLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.loginAttempts = 0;
        state.lastLoginAttempt = null;

        if (action.payload.twoFactorRequired) {
          state.twoFactorRequired = true;
          state.twoFactorToken = action.payload.twoFactorToken;
          state.twoFactorSetupRequired = action.payload.twoFactorSetupRequired || false;
          state.twoFactorQrCode = action.payload.twoFactorQrCode || null;
          state.twoFactorSecret = action.payload.twoFactorSecret || null;
        } else {
          state.user = action.payload.user;
          state.company = action.payload.company;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.isAuthenticated = true;
          state.twoFactorRequired = false;
          state.twoFactorToken = null;
          state.twoFactorSetupRequired = false;
          state.twoFactorQrCode = null;
          state.twoFactorSecret = null;
        }
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Verificação 2FA
    builder
      .addCase(verify2FA.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verify2FA.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (action.payload.user) {
          state.user = action.payload.user;
        }
        if (action.payload.company) {
          state.company = action.payload.company;
        }
        if (action.payload.token) {
          state.token = action.payload.token;
          state.isAuthenticated = true;
        }
        if (action.payload.refreshToken) {
          state.refreshToken = action.payload.refreshToken;
        }
        state.twoFactorRequired = false;
        state.twoFactorToken = null;
        state.twoFactorSetupRequired = false;
        state.twoFactorQrCode = null;
        state.twoFactorSecret = null;
        state.loginAttempts = 0;
        state.lastLoginAttempt = null;
      })
      .addCase(verify2FA.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Registro
    builder
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Logout
    builder
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.company = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
        state.twoFactorRequired = false;
        state.twoFactorToken = null;
        state.loginAttempts = 0;
        state.lastLoginAttempt = null;
      });

    // Refresh token
    builder
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      })
      .addCase(refreshToken.rejected, (state) => {
        // Se refresh falhou, deslogar
        state.user = null;
        state.company = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
      });

    // Carregar dados do usuário
    builder
      .addCase(loadUserData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadUserData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.company = action.payload.company;
      })
      .addCase(loadUserData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Esqueci minha senha
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Reset de senha
    builder
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clear2FA,
  setAuthData,
  updateUser,
  updateCompany,
  incrementLoginAttempts,
  resetLoginAttempts,
} = authSlice.actions;

// =====================================================
// SELECTORS
// =====================================================

export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectCurrentCompany = (state: { auth: AuthState }) => state.auth.company;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectLoginAttempts = (state: { auth: AuthState }) => state.auth.loginAttempts;
export const selectLastLoginAttempt = (state: { auth: AuthState }) => state.auth.lastLoginAttempt;
export const selectTwoFactorRequired = (state: { auth: AuthState }) => state.auth.twoFactorRequired;
export const selectTwoFactorToken = (state: { auth: AuthState }) => state.auth.twoFactorToken;
export const selectTwoFactorSetupRequired = (state: { auth: AuthState }) => state.auth.twoFactorSetupRequired;
export const selectTwoFactorQrCode = (state: { auth: AuthState }) => state.auth.twoFactorQrCode;
export const selectTwoFactorSecret = (state: { auth: AuthState }) => state.auth.twoFactorSecret;

export default authSlice.reducer;